import React, { useState, useRef, useEffect } from 'react';
import './ChatbotSidebar.css';
import aiAgentSparkle from '../assets/ai-agent-sparkle.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { getApiUrl } from '../utils/api';
import { auth } from '../firebase';
import { authFetch } from '../utils/authFetch';
import { useProject } from '../contexts/ProjectContext';
import { isAdminUserEmail, isChatbotUserEmail } from '../utils/adminAccess';
import { trackEvent } from '../statsig';
import { ChatTarget, CreditReview, ProductionHubReview, PreviewValue, settingLabel, targetLabel, actionLabel } from './ProductionHubReview';

function fixMarkdownTables(text: string): string {
  // Remove <p> tags that wrap table lines
  return text.replace(/<p>([\s\S]*?)<\/p>/g, (match, inner) => {
    if (inner.trim().startsWith('|')) {
      return inner.trim();
    }
    return match;
  });
}

function isRightToLeftText(text: string): boolean {
  const firstRtlCharacter = text.search(/[\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufefc]/);
  const firstLtrCharacter = text.search(/[A-Za-z\u00c0-\u02af]/);

  return firstRtlCharacter !== -1 && (firstLtrCharacter === -1 || firstRtlCharacter < firstLtrCharacter);
}

interface Edit {
  sheet?: string;
  row: number;
  col: number;
  value: string | number | boolean;
  label?: string;
  block?: string;
  expectedValue?: string;
  currentValue?: string;
  setting?: string;
  cardNumber?: number | null;
}

interface DocumentImportAction {
  type: string;
  block?: string;
  role?: string;
  value?: string;
  from?: string;
  to?: string;
  template?: string | null;
  column_count?: number | null;
  entries?: Array<{ role: string; values: string[] }>;
}

interface DocumentImportPlan {
  baseSnapshot: string;
  combinedRows: string[][];
  actions: DocumentImportAction[];
}

type UndoSnapshot = Edit[] | { kind: 'document_import' | 'project_structure'; combinedRows: string[][]; expectedSnapshot?: string };

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  pendingEdits?: Edit[];
  pendingImport?: DocumentImportPlan;
  pendingStructure?: DocumentImportPlan;
  confirmed?: boolean;
  snapshot?: UndoSnapshot;   // stores original values or the complete credit-source import state for undo
  isRetryable?: boolean;
  isStreaming?: boolean;
  requestText?: string;
  documentName?: string;
  requestTarget?: ChatTarget | null;
  requestSelection?: ChatTarget | null;
  requestMode?: 'review';
  review?: CreditReview;
}

interface ChatbotSidebarProps {
  sheetsUrl?: string | null;
  onLoadGoogleSheets?: () => void;
  canEdit?: boolean;
  selectedTarget?: ChatTarget | null;
}

interface SpeechRecognitionResultLike extends ArrayLike<{ transcript: string }> {
  isFinal: boolean;
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((event: { resultIndex: number; results: ArrayLike<SpeechRecognitionResultLike> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

const ThinkingIndicator: React.FC = () => (
  <span className="thinking-indicator" role="status" aria-live="polite">
    Thinking
  </span>
);

const AttachmentIcon: React.FC = () => (
  <svg viewBox="0 0 27 27" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m21.4 11.6-8.5 8.5a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.2-8.2" />
  </svg>
);

const DocumentIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6M8 13h8M8 17h6" />
  </svg>
);

const ChatbotSidebar: React.FC<ChatbotSidebarProps> = ({ sheetsUrl, onLoadGoogleSheets, canEdit = true, selectedTarget = null }) => {
  const { i18n } = useLingui();
  const textDirection = i18n.locale === 'he' ? 'rtl' : 'ltr';
  const userEmail = auth.currentUser?.email?.toLowerCase() ?? '';
  const { selectedProject } = useProject();
  const [conversationTarget, setConversationTarget] = useState<ChatTarget | null>(null);
  const requestController = useRef<AbortController | null>(null);
  const conversationMemory = useRef<{ summary: string; throughId: string | null }>({ summary: '', throughId: null });
  const welcomeMessage = t`Production Assistant online. Ready to help with your project. What scene are we working on today?`;
  const suggestions = [
    t`Show me a table of all blocks`,
    t`What are the video settings?`,
    t`How many cast members are there?`,
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: welcomeMessage,
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [speechStatus, setSpeechStatus] = useState('');
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speechBaseTextRef = useRef('');
  const speechFinalTextRef = useRef('');
  const speechErroredRef = useRef(false);
  const speechRecognitionConstructor = getSpeechRecognitionConstructor();
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  useEffect(() => {
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
    }
    requestController.current?.abort();
    speechRecognitionRef.current = null;
    setIsListening(false);
    setSpeechStatus('');
    speechErroredRef.current = false;
    conversationMemory.current = { summary: '', throughId: null };
    setConversationTarget(null);
    setHasInteracted(false);
    setSelectedDocument(null);
    setActionsAnchor(null);
    setInputValue('');
    setIsTyping(false);
    setMessages([{ id: Date.now().toString(), text: welcomeMessage, sender: 'bot', timestamp: new Date() }]);
    // Reset project-scoped chat independently of locale changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);
  useEffect(() => () => {
    requestController.current?.abort();
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
    }
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const updateScrollButtonVisibility = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollHeight > container.clientHeight + 1;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(hasOverflow && distanceFromBottom > 100);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resizeInput = () => {
    const input = inputRef.current;
    if (!input) return;
    
    const MAX_INPUT_HEIGHT = 120;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, MAX_INPUT_HEIGHT);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    updateScrollButtonVisibility();
    const resizeObserver = new ResizeObserver(updateScrollButtonVisibility);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [isOpen, messages, isTyping]);

  useEffect(() => {
    resizeInput();
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (!isOpen && isListening) speechRecognitionRef.current?.abort();
  }, [isOpen, isListening]);

  useEffect(() => {
    if (!hasInteracted) {
      setMessages([{
        id: Date.now().toString(),
        text: welcomeMessage,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    }
  }, [hasInteracted, i18n.locale, welcomeMessage]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      resizeInput();
    }
  }, [isOpen]);

  // ============================================
  // CORE SEND LOGIC — shared by handleSendMessage,
  // handleSuggestionClick, and handleRetry
  // ============================================
  const sendMessage = async (
    text: string,
    history: { role: string; content: string; id?: string }[],
    source: 'input' | 'suggestion' | 'retry',
    document: File | null = null,
    requestTarget: ChatTarget | null = conversationTarget,
    requestSelection: ChatTarget | null = selectedTarget,
    requestMode?: 'review',
  ) => {
    const controller = new AbortController();
    requestController.current = controller;
    setIsTyping(true);
    let botMessageId = '';
    let didSucceed = false;
    const retainSummary = (update: { summary: string; coveredMessages: number }) => {
      const throughId = history[update.coveredMessages - 1]?.id;
      if (throughId && typeof update.summary === 'string') conversationMemory.current = { summary: update.summary, throughId };
    };
    const receiveMetadata = (event: string, data: string) => {
      if (event !== 'chat_target' && event !== 'credit_review' && event !== 'history_summary') return false;
      const parsed = JSON.parse(data);
      if (event === 'history_summary') retainSummary(parsed);
      else if (event === 'chat_target') setConversationTarget(parsed.target ?? null);
      else setMessages(prev => prev.map(message => message.id === botMessageId
        ? { ...message, review: parsed, text: t`Credit review completed.`, isStreaming: false } : message));
      return true;
    };
    let serverError: { friendly?: string; retryable?: boolean; status_code?: number } | undefined;
    const streamError = (data: string): Error => {
      let payload;
      try {
        payload = JSON.parse(data);
      } catch {
        payload = { message: data };
      }
      serverError = payload ?? {};
      return new Error(payload?.message || 'Stream error');
    };

    trackEvent('chatbot_message_sent', {
      source,
      message_length: text.length,
      history_length: history.length,
      has_document: Boolean(document),
    });

    try {
      const payload = {
        input: text,
        sheetUrl: sheetsUrl ?? '',
        projectId: selectedProject?.id ?? '',
        history,
        historySummary: conversationMemory.current.summary,
        stream: true,
        conversationTarget: requestTarget,
        selectedTarget: requestSelection,
        requestMode,
      };
      const request = document
        ? (() => {
            const formData = new FormData();
            formData.append('input', text);
            formData.append('projectId', selectedProject?.id ?? '');
            formData.append('history', JSON.stringify(history));
            formData.append('historySummary', conversationMemory.current.summary);
            formData.append('stream', 'true');
            formData.append('document', document);
            formData.append('conversationTarget', JSON.stringify(requestTarget));
            formData.append('selectedTarget', JSON.stringify(requestSelection));
            if (requestMode) formData.append('requestMode', requestMode);
            return { method: 'POST', body: formData };
          })()
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          };

      const response = await authFetch(`${getApiUrl()}/chatbot`, { ...request, signal: controller.signal });
      if (controller.signal.aborted) return false;

      const contentType = response.headers.get('Content-Type') || '';

      if (response.ok && contentType.includes('text/event-stream')) {
        botMessageId = (Date.now() + 1).toString();
        
        // Add initial message with isStreaming: true
        setMessages((prev) => [...prev, {
          id: botMessageId,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          isStreaming: true,
        }]);

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Readable stream not supported or body is null');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (controller.signal.aborted) return false;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            if (!block.trim()) continue;
            let eventName = '';
            let dataText = '';
            const lines = block.split('\n');
            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventName = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                dataText = line.substring(5).trim();
              }
            }

            if (receiveMetadata(eventName, dataText)) continue;
            if (eventName === 'token') {
              try {
                const parsed = JSON.parse(dataText);
                accumulatedText += parsed.text || '';
                const formattedText: string = fixMarkdownTables(accumulatedText.replace(/\\n/g, '\n'));

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId
                      ? { ...msg, text: formattedText }
                      : msg
                  )
                );
              } catch (e) {
                console.error('Error parsing token data:', e);
              }
            } else if (eventName === 'error') {
              await reader.cancel().catch(() => undefined);
              throw streamError(dataText);
            } else if (eventName === 'edit_preview') {
              try {
                const parsed = JSON.parse(dataText);
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId
                      ? {
                          ...msg,
                          text: parsed.summary || parsed.message || '',
                          pendingEdits: parsed.edits?.length ? parsed.edits : undefined,
                          pendingImport: parsed.importPlan,
                          pendingStructure: parsed.structuralPlan,
                          isStreaming: false,
                        }
                      : msg
                  )
                );
              } catch (e) {
                console.error('Error parsing edit preview data:', e);
              }
            } else if (eventName === 'done') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                )
              );
            }
          }
        }

        // Final remaining buffer
        if (buffer.trim()) {
          let eventName = '';
          let dataText = '';
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventName = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
              dataText = line.substring(5).trim();
            }
          }
          if (receiveMetadata(eventName, dataText)) {
            // Structured events may also be the final unterminated SSE frame.
          } else if (eventName === 'token') {
            try {
              const parsed = JSON.parse(dataText);
              accumulatedText += parsed.text || '';
              const streamedText = fixMarkdownTables(accumulatedText.replace(/\\n/g, '\n'));
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? { ...msg, text: streamedText }
                    : msg
                )
              );
            } catch (e) {}
          } else if (eventName === 'error') {
            throw streamError(dataText);
          } else if (eventName === 'edit_preview') {
            try {
              const parsed = JSON.parse(dataText);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? {
                        ...msg,
                        text: parsed.summary || parsed.message || '',
                        pendingEdits: parsed.edits?.length ? parsed.edits : undefined,
                        pendingImport: parsed.importPlan,
                        pendingStructure: parsed.structuralPlan,
                        isStreaming: false,
                      }
                    : msg
                )
              );
            } catch (e) {}
          }
        }

        // Finalize streaming state
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? { ...msg, isStreaming: false }
              : msg
          )
        );

        trackEvent('chatbot_response_received', {
          source,
          response_type: 'stream',
        });

      } else {
        // Fallback for standard JSON
        const result = await response.json();
        if (controller.signal.aborted) return false;
        if (!response.ok || result.status === 'ERROR') {
          serverError = { ...result, status_code: response.ok ? undefined : response.status };
          throw new Error(result.detail || result.message || response.statusText);
        }

        if ('target' in result) setConversationTarget(result.target ?? null);
        if (result.historySummary) retainSummary(result.historySummary);
        if (result.review) {
          setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), text: t`Credit review completed.`, sender: 'bot', timestamp: new Date(), review: result.review }]);
        } else if (result.status === 'PENDING') {
          // Edit preview — show confirmation bubble
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: result.message,
            sender: 'bot',
            timestamp: new Date(),
            pendingEdits: result.edits?.length ? result.edits : undefined,
            pendingImport: result.importPlan,
            pendingStructure: result.structuralPlan,
          }]);

        } else if (result.status === 'OK') {
          let botText: string;
          if (Array.isArray(result.message)) {
            botText = result.message
              .map((edit: Edit) => `Row ${edit.row}, Col ${edit.col}: ${edit.value}`)
              .join('\n');
          } else if (typeof result.message === 'string') {
            botText = fixMarkdownTables(result.message.replace(/\\n/g, '\n'));
          } else {
            botText = String(result.message);
          }
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: botText,
            sender: 'bot',
            timestamp: new Date(),
          }]);

        } else {
          // Fallback for unexpected statuses
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: 'Unexpected response from server.',
            sender: 'bot',
            timestamp: new Date(),
            isRetryable: true,
          }]);
        }

        trackEvent('chatbot_response_received', {
          source,
          response_type: 'json',
          status: result.status ?? 'UNKNOWN',
        });
      }
      didSucceed = response.ok;
    } catch (err) {
      if (controller.signal.aborted) return false;
      const detail = err instanceof Error ? err.message : String(err);
      trackEvent('chatbot_response_failed', {
        source,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      const status = Number(detail.match(/Error code:\s*(\d{3})/i)?.[1]) || serverError?.status_code;
      const retryable = serverError?.retryable ?? (!status || status === 408 || status === 429 || status >= 500);
      const friendly = status && status >= 400 && status < 500 && !retryable
        ? 'The AI API rejected the request. Please contact support.'
        : serverError
          ? serverError.friendly || 'The server returned an error. Please try again in a moment.'
          : err instanceof TypeError
            ? 'Network error. Check your connection and try again.'
            : 'An unexpected error occurred.';
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `**${friendly}**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: false,
        isRetryable: retryable,
      };
      setMessages(prev => prev.some(message => message.id === botMessageId)
        ? prev.map(message => message.id === botMessageId
          ? { ...message, text: message.text ? `${message.text}\n\n${errorMessage.text}` : errorMessage.text,
              isStreaming: false, isRetryable: retryable }
          : message)
        : [...prev, errorMessage]);
    } finally {
      if (requestController.current === controller) setIsTyping(false);
    }
    return didSucceed;
  };

  // ============================================
  // SEND MESSAGE — triggered by input
  // ============================================
  const buildHistory = (items: Message[] = messages) => {
    const through = items.findIndex(message => message.id === conversationMemory.current.throughId);
    return items.slice(through + 1).filter(message => !message.isRetryable).map(message => ({
      id: message.id,
      role: message.sender === 'user' ? 'user' : 'assistant',
      content: message.review
        ? `${message.text}\nSaved credit-review findings (verify current values before edits): ${JSON.stringify(message.review)}`
        : `${message.text}${message.sender === 'user' && message.requestSelection ? `\nSelection at request: ${JSON.stringify(message.requestSelection)}` : ''}${Boolean(message.pendingEdits?.length || message.pendingImport || message.pendingStructure) ? `\nChange status: ${message.confirmed === true ? 'applied' : message.confirmed === false ? 'cancelled' : 'proposed, not applied'}. Details: ${JSON.stringify(message.pendingEdits?.length ? message.pendingEdits : (message.pendingImport ?? message.pendingStructure)?.actions)}` : ''}`,
    }));
  };

  const handleSendMessage = async () => {
    if (isListening) return;
    const trimmedInput = inputValue.trim();
    if (!trimmedInput && !selectedDocument) return;
    const requestText = trimmedInput || 'Import this credit source faithfully. Use it as the source of truth for credits and adapt the project structure when needed.';
    const document = selectedDocument;

    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: requestText,
      sender: 'user',
      timestamp: new Date(),
      requestText,
      documentName: document?.name,
      requestTarget: conversationTarget,
      requestSelection: selectedTarget,
    }]);
    setInputValue('');
    setSelectedDocument(null);
    if (documentInputRef.current) documentInputRef.current.value = '';

    const history = buildHistory();

    await sendMessage(requestText, history, 'input', document);
  };

  // ============================================
  // SUGGESTION CLICK — triggered by suggestion chips
  // ============================================
  const handleSuggestionClick = async (suggestion: string, requestMode?: 'review') => {
    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: suggestion,
      sender: 'user',
      timestamp: new Date(),
      requestTarget: conversationTarget,
      requestSelection: selectedTarget,
      requestMode,
    }]);

    await sendMessage(suggestion, buildHistory(), 'suggestion', null, conversationTarget, selectedTarget, requestMode);
  };

  // ============================================
  // RETRY — resends last user message after error
  // ============================================
  const handleRetry = async () => {
    const lastUserMessage = [...messages].reverse().find((m) => m.sender === 'user');
    if (!lastUserMessage) return;

    // Remove all retryable error messages
    setMessages((prev) => prev.filter((m) => !m.isRetryable));

    const history = buildHistory(messages.slice(0, messages.indexOf(lastUserMessage)));

    const retryDocument = lastUserMessage.documentName === selectedDocument?.name ? selectedDocument : null;
    const didSucceed = await sendMessage(lastUserMessage.requestText ?? lastUserMessage.text, history, 'retry', retryDocument,
      lastUserMessage.requestTarget ?? null, lastUserMessage.requestSelection ?? null, lastUserMessage.requestMode);
    if (retryDocument && didSucceed) {
      setSelectedDocument(null);
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  // ============================================
  // CONFIRM EDIT — applies pending edits to sheet
  // ============================================
  const handleConfirmEdit = async (
    messageId: string,
    edits: Edit[],
    importPlan?: DocumentImportPlan,
    structuralPlan?: DocumentImportPlan,
  ) => {
    setIsTyping(true);

    trackEvent('chatbot_edit_confirmation_requested', {
      edit_count: edits.length,
    });

    try {
      const response = await authFetch(`${getApiUrl()}/chatbot/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sheetUrl: sheetsUrl ?? '',
        projectId: selectedProject?.id ?? '',
        edits,
        importPlan,
        structuralPlan,
      }),
      });

      const result = await response.json();

      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
        snapshot: result.status === 'OK' ? result.snapshot : undefined,
      }]);
      if (result.status === 'OK') {
        trackEvent('chatbot_edits_applied', {
          edit_count: edits.length,
        });
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, confirmed: true } : m))
        );
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      trackEvent('chatbot_edits_apply_failed', {
        edit_count: edits.length,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to apply edits. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // ============================================
  // CANCEL EDIT — discards pending edits
  // ============================================
  const handleCancelEdit = (messageId: string) => {
    const editCount = messages.find((message) => message.id === messageId)?.pendingEdits?.length ?? 0;
    trackEvent('chatbot_edits_cancelled', { edit_count: editCount });

    // Mark as cancelled and add a cancel message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, confirmed: false } : m))
    );
    setMessages((prev) => [...prev, {
      id: (Date.now() + 1).toString(),
      text: 'Edit cancelled. No changes were made.',
      sender: 'bot',
      timestamp: new Date(),
    }]);
  };

  const handleScroll = () => {
    updateScrollButtonVisibility();
  };

  // ============================================
  // UNDO EDIT — restores cells to original values
  // ============================================
  const handleUndo = async (messageId: string, snapshot: UndoSnapshot) => {
    trackEvent('chatbot_undo_requested', { edit_count: Array.isArray(snapshot) ? snapshot.length : 1 });

    // Remove the undo button from this message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, snapshot: undefined } : m))
    );
    setIsTyping(true);

    try {
      const response = await authFetch(`${getApiUrl()}/chatbot/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl: sheetsUrl ?? '', projectId: selectedProject?.id ?? '', snapshot }),
      });

      const result = await response.json();
      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
      }]);
      if (result.status === 'OK') {
        trackEvent('chatbot_undo_completed', { edit_count: Array.isArray(snapshot) ? snapshot.length : 1 });
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      trackEvent('chatbot_undo_failed', {
        edit_count: Array.isArray(snapshot) ? snapshot.length : 1,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to undo. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleClearChat = () => {
    conversationMemory.current = { summary: '', throughId: null };
    requestController.current?.abort();
    setConversationTarget(null);
    setIsTyping(false);
    trackEvent('chatbot_cleared', { message_count: messages.length });
    setMessages([{
      id: Date.now().toString(),
      text: welcomeMessage,
      sender: 'bot',
      timestamp: new Date(),
    }]);
    setHasInteracted(false);
  };

  const handleExportChat = () => {
    const exportedAt = new Date();
    const chatExport = {
      exportedAt: exportedAt.toISOString(),
      conversationSummary: conversationMemory.current.summary,
      project: {
        id: selectedProject?.id ?? null,
        sheetsUrl: sheetsUrl ?? null,
      },
      messages,
    };
    const downloadUrl = URL.createObjectURL(
      new Blob([JSON.stringify(chatExport, null, 2)], { type: 'application/json' })
    );
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = `production-hub-chat-${exportedAt.toISOString().replace(/[:.]/g, '-')}.json`;
    downloadLink.click();
    URL.revokeObjectURL(downloadUrl);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isTyping && !isListening) {
        handleSendMessage();
      }
    }
  };

  const handleVoiceInput = () => {
    if (isListening) {
      speechRecognitionRef.current?.stop();
      return;
    }
    if (!speechRecognitionConstructor) {
      setSpeechStatus(t`Voice input isn't supported by this browser.`);
      return;
    }

    try {
      const recognition = new speechRecognitionConstructor();
      const appLanguage = i18n.locale;
      const languageName = appLanguage === 'he' ? 'עברית' : appLanguage === 'pl' ? 'Polski' : 'English';
      speechBaseTextRef.current = inputValue.trimEnd();
      speechFinalTextRef.current = '';
      speechErroredRef.current = false;
      recognition.lang = appLanguage === 'he' ? 'he-IL' : appLanguage === 'pl' ? 'pl-PL' : 'en-US';
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;
      recognition.onstart = () => {
        setIsListening(true);
        setSpeechStatus(t`Listening… Select the microphone to finish.`);
      };
      recognition.onresult = (event) => {
        let interimText = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index];
          const transcript = result[0]?.transcript ?? '';
          if (result.isFinal) speechFinalTextRef.current += transcript;
          else interimText += transcript;
        }
        const base = speechBaseTextRef.current;
        const recognized = `${speechFinalTextRef.current}${interimText}`.trim();
        setInputValue([base, recognized].filter(Boolean).join(base ? ' ' : ''));
      };
      recognition.onerror = (event) => {
        if (event.error === 'aborted') {
          setIsListening(false);
          speechRecognitionRef.current = null;
          return;
        }
        speechErroredRef.current = true;
        const status = event.error === 'not-allowed' || event.error === 'service-not-allowed'
          ? t`Microphone access was blocked. Check your browser permissions.`
          : event.error === 'language-not-supported' || event.error === 'language-unavailable'
            ? t`Voice recognition doesn't support the app language in this browser.`
          : event.error === 'no-speech'
            ? t`No audio was detected. Voice input follows the app language: ${languageName}.`
            : event.error === 'audio-capture'
              ? t`No microphone was found.`
              : event.error === 'network'
                ? t`Voice input couldn't connect. Check your connection and try again.`
                : t`Voice input stopped. Your draft is still here.`;
        setSpeechStatus(status);
        setInputValue([speechBaseTextRef.current, speechFinalTextRef.current.trim()].filter(Boolean).join(speechBaseTextRef.current ? ' ' : ''));
        setIsListening(false);
        speechRecognitionRef.current = null;
      };
      recognition.onend = () => {
        setInputValue([speechBaseTextRef.current, speechFinalTextRef.current.trim()].filter(Boolean).join(speechBaseTextRef.current ? ' ' : ''));
        setIsListening(false);
        speechRecognitionRef.current = null;
        if (!speechErroredRef.current) setSpeechStatus('');
        speechErroredRef.current = false;
      };
      speechRecognitionRef.current = recognition;
      setSpeechStatus('');
      setIsListening(true);
      recognition.start();
    } catch {
      speechRecognitionRef.current = null;
      setIsListening(false);
      setSpeechStatus(t`Voice input couldn't start. Check your microphone permissions and try again.`);
    }
  };

  if (!isChatbotUserEmail(userEmail)) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <Tooltip title={isOpen ? t`Close Production Hub` : t`Open Production Hub`} placement="top">
        <button
          className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
          onClick={() => {
            const willOpen = !isOpen;
            setIsOpen(willOpen);
            trackEvent(willOpen ? 'chatbot_opened' : 'chatbot_closed');
          }}
          aria-label="Toggle chatbot"
        >
          <img className="icon-chat" src={aiAgentSparkle} alt="" aria-hidden="true" />
          <svg className="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </Tooltip>

      {/* Sidebar */}
      <div className={`chatbot-sidebar ${isOpen ? 'open' : ''}`} dir={textDirection} lang={i18n.locale}>
        <div className="chatbot-header">
          <div className="header-content">
            <div className="header-actions">
              <Tooltip title={t`Clear chat`} placement="top">
                <button className="clear-btn" onClick={handleClearChat} aria-label="Clear chat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              </Tooltip>
              {isAdminUserEmail(userEmail) && (
                <Tooltip title={t`Export chat as JSON`} placement="top">
                  <button className="export-chat-btn" onClick={handleExportChat} aria-label="Export chat as JSON">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 3v12" />
                      <path d="m7 10 5 5 5-5" />
                      <path d="M5 21h14" />
                    </svg>
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="logo-section">
              <div className="header-text">
                <h3>Production Hub</h3>
                <div className="status-line">
                  <span className="status-dot"></span>
                  <span className="status-text">In Production</span>
                </div>
              </div>
            </div>
          </div>
          <div className="scan-line"></div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender} ${isRightToLeftText(message.text) ? 'rtl-message' : ''}`}>
              <div className="message-content">
                {message.documentName && (
                  <div className="message-document" title={message.documentName}>
                    <DocumentIcon />
                    <span>{message.documentName}</span>
                  </div>
                )}
                <div
                  className={`message-bubble ${Boolean(message.pendingEdits?.length || message.pendingImport || message.pendingStructure) ? 'pending' : ''} ${message.isStreaming ? 'streaming' : ''}`}
                  dir="auto"
                >
                  {message.text ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      remarkRehypeOptions={{ allowDangerousHtml: true }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  ) : null}

                  {/* Pending edits table */}
                  {message.review && <ProductionHubReview review={message.review} />}
                  {Boolean(message.pendingEdits?.length || message.pendingImport || message.pendingStructure) && message.confirmed === undefined && (
                    <div className="edit-preview">
                      {message.pendingImport || message.pendingStructure ? (
                        <div className="edit-preview-table-wrapper">
                          <table className="edit-preview-table">
                            <thead><tr><th>{t`Action`}</th><th>{t`Target`}</th><th>{t`Details`}</th></tr></thead>
                            <tbody>{(message.pendingImport ?? message.pendingStructure)!.actions.map((action, i) => (
                              <tr key={i}>
                                <td>{actionLabel(action.type)}</td>
                                <td dir="auto">{action.block ?? action.to ?? t`Project`}{action.role && <small className="hub-credit-role">{action.role}</small>}</td>
                                <td>{action.from !== undefined && <><PreviewValue value={action.from} /> → </>}<PreviewValue value={action.value ?? action.to ?? (action.column_count ? `${action.column_count} columns` : undefined) ?? action.entries?.map((entry) => `${entry.role}: ${entry.values.join(', ')}`).join('; ') ?? '—'} />{action.template && <span>{t`Formatting source`}: {action.template}</span>}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                      ) : <div className="edit-preview-table-wrapper">
                        <table className="edit-preview-table">
                          <thead>
                            <tr>
                              <th>{t`Target`}</th>
                              <th>{t`Setting or credit`}</th>
                              <th>{t`Current`}</th>
                              <th>{t`Proposed`}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {message.pendingEdits?.map((edit, i) => (
                              <tr key={i}>
                                <td dir="auto">{edit.block ? targetLabel({ block: edit.block === 'Video settings' ? t`Video settings` : edit.block, cardNumber: edit.cardNumber }) : edit.sheet ?? '—'}</td>
                                <td dir="auto">{settingLabel(edit.setting)}{edit.label && <small className="hub-credit-role">{edit.label}</small>}</td>
                                <td><PreviewValue value={edit.currentValue ?? edit.expectedValue} /></td>
                                <td><PreviewValue value={edit.value} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>}
                      <div className="edit-confirm-buttons">
                        {canEdit && <button
                          className="confirm-btn"
                          onClick={() => handleConfirmEdit(
                            message.id,
                            message.pendingEdits ?? [],
                            message.pendingImport,
                            message.pendingStructure,
                          )}
                        >
                          ✓ Confirm
                        </button>}
                        <button
                          className="cancel-btn"
                          onClick={() => handleCancelEdit(message.id)}
                        >
                          ✗ Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolved state */}
                  {Boolean(message.pendingEdits?.length || message.pendingImport || message.pendingStructure) && message.confirmed === true && (
                    <div className="edit-resolved confirmed">✓ Applied</div>
                  )}
                  {Boolean(message.pendingEdits?.length || message.pendingImport || message.pendingStructure) && message.confirmed === false && (
                    <div className="edit-resolved cancelled">✗ Cancelled</div>
                  )}

                  {/* Undo button — shown after a successful edit apply */}
                  {canEdit && message.snapshot && (
                    <button
                      className="undo-btn"
                      onClick={() => handleUndo(message.id, message.snapshot!)}
                    >
                      ↩ Undo
                    </button>
                  )}

                  {/* Retry button — shown on error messages */}
                  {message.isRetryable && (
                    <button className="retry-btn" onClick={handleRetry}>
                      ↺ Retry
                    </button>
                  )}
                </div>

                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && !messages.some((message) => message.isStreaming) && (
            <div className="message bot typing">
              <div className="message-content">
                <div className="message-bubble" dir="auto">
                  <ThinkingIndicator />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          {showScrollButton && (
            <Tooltip title={t`Jump to latest message`} placement="top">
              <button
                className="scroll-bottom-btn"
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </Tooltip>
          )}
        </div>

        {/* Suggestion chips — only before first interaction */}
        {!hasInteracted && (
          <div className="suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="chatbot-input">
          <div className="hub-context-bar">
            {selectedTarget && <span dir="auto">{t`Selected block`}: <bdi>{targetLabel(selectedTarget)}</bdi></span>}
          </div>
          {selectedDocument && (
            <div className="document-selection">
              <span title={selectedDocument.name}><AttachmentIcon />{selectedDocument.name}</span>
              <Tooltip title={t`Remove credit source`} placement="top">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDocument(null);
                    if (documentInputRef.current) documentInputRef.current.value = '';
                  }}
                  aria-label="Remove credit source"
                >
                  ×
                </button>
              </Tooltip>
            </div>
          )}
          <div className="chatbot-input-wrapper">
            <input
              ref={documentInputRef}
              className="document-input"
              type="file"
              accept=".docx,.pdf,.xlsx,.xlsm,.csv,.txt,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroenabled.12,text/csv,text/plain"
              onChange={(event) => setSelectedDocument(event.target.files?.[0] ?? null)}
              aria-label="Attach credit source"
            />
            <Tooltip title={actionsAnchor ? '' : isTyping ? t`Wait for the response` : t`More actions`} placement="top" disableHoverListener={Boolean(actionsAnchor)} disableFocusListener={Boolean(actionsAnchor)}>
              <span className="chatbot-tooltip-anchor">
                <button
                  type="button"
                  className="document-attach-button"
                  onClick={(event) => setActionsAnchor(event.currentTarget)}
                  disabled={isTyping || isListening}
                  aria-label={t`More actions`}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(actionsAnchor)}
                  aria-controls={actionsAnchor ? 'production-hub-actions' : undefined}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              </span>
            </Tooltip>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isListening}
              placeholder="Ask about your production..."
              className="message-input"
              dir="auto"
              lang={i18n.locale}
              rows={1}
            />
            <Tooltip title={isListening ? t`Stop voice input` : speechRecognitionConstructor ? t`Use voice input` : t`Voice input isn't supported by this browser.`} placement="top">
              <span className="chatbot-tooltip-anchor">
                <button
                  type="button"
                  className={`voice-input-button${isListening ? ' listening' : ''}`}
                  onClick={handleVoiceInput}
                  disabled={isTyping || (!isListening && !speechRecognitionConstructor)}
                  aria-label={isListening ? t`Stop voice input` : speechRecognitionConstructor ? t`Use voice input` : t`Voice input unavailable`}
                  aria-pressed={isListening}
                  title={speechRecognitionConstructor ? undefined : t`Voice input isn't supported by this browser.`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="2.5" width="6" height="12" rx="3" />
                    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3.5M8.5 21.5h7" />
                  </svg>
                </button>
              </span>
            </Tooltip>
            <Tooltip title={isTyping ? t`Wait for the response` : t`Send message`} placement="top">
              <span className="chatbot-tooltip-anchor">
                <button
                  className="send-button"
                  onClick={handleSendMessage}
                  disabled={isTyping || isListening || (!inputValue.trim() && !selectedDocument)}
                  aria-label="Send message"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </span>
            </Tooltip>
          </div>
          {(speechStatus || !speechRecognitionConstructor) && <div className={`voice-input-status${isListening ? ' active' : ''}`} role="status" aria-live="polite">{speechStatus || t`Voice input isn't supported by this browser.`}</div>}
          <Menu
            id="production-hub-actions"
            className="hub-actions-menu"
            anchorEl={actionsAnchor}
            open={Boolean(actionsAnchor)}
            onClose={() => setActionsAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: textDirection === 'rtl' ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: textDirection === 'rtl' ? 'right' : 'left' }}
            PaperProps={{ sx: { mt: -1 } }}
            MenuListProps={{ 'aria-label': t`More actions`, dir: textDirection }}
          >
            <MenuItem onClick={() => { setActionsAnchor(null); documentInputRef.current?.click(); }}>
              <svg className="hub-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" />
                <path d="M14 3v6h6M12 17v-5m-2 2 2-2 2 2" />
              </svg>
              <span>{t`Upload file`}</span>
            </MenuItem>
            <MenuItem disabled={!selectedProject?.id || isTyping} onClick={() => { setActionsAnchor(null); void handleSuggestionClick(t`Check my credits`, 'review'); }}>
              <svg className="hub-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" />
                <path d="M14 3v6h6m-12 7 2 2 5-5" />
              </svg>
              <span>{t`Check credits`}</span>
            </MenuItem>
          </Menu>
          <div className="terminal-footer">
            <span className="footer-text">Enter to send | Shift+Enter for newline</span>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && <div className="chatbot-backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default ChatbotSidebar;
